import { useState } from 'react';
import useProducts from '~/features/commerce-api/hooks/useProducts';

/**
 * Custom hook to check for non-standard or long delivery estimates
 * which is anything other than 1-2 hverdage
 *
 * @param {string} [shipmentId] - Optional shipment ID to check products for specific shipment only
 * @returns {Object} An object containing:
 * - hasNonStandardDelivery: Function to check if any products have non-standard delivery times
 * - longestDeliveryEstimate: The longest delivery estimate found
 * - isDeliveryDialogOpen: Boolean state for dialog visibility
 * - setIsDeliveryDialogOpen: Function to set dialog visibility
 */
export const useDeliveryEstimate = (shipmentId?: string) => {
    const { products: productsInBasket, getProductsByShipment } = useProducts();
    const [isDeliveryDialogOpen, setIsDeliveryDialogOpen] = useState(false);
    const [longestDeliveryEstimate, setLongestDeliveryEstimate] = useState('');

    /**
     * Check if any products have non-standard delivery times
     * @returns {boolean} True if any products have non-standard delivery
     */
    const hasNonStandardDelivery = () => {
        // Get products for specific shipment if shipmentId is provided, otherwise get all products
        const products = shipmentId ? getProductsByShipment(shipmentId) : productsInBasket;

        if (!products || !products.length) return false;

        let hasNonStandard = false;
        let longestEstimate = '1-2';

        // For debugging
        console.log(
            `Checking products for non-standard delivery${shipmentId ? ` (shipment: ${shipmentId})` : ''}:`,
            products,
        );

        products.forEach((product) => {
            // Use the c_deliveryDaysText field, which contains strings like "Afsendes 1-4 hverdage"
            if (product.c_deliveryDaysText) {
                console.log(
                    `Product ${product.id} has c_deliveryDaysText: ${product.c_deliveryDaysText}`,
                );

                // Extract the numbers from the text (1-4, 3-5, etc.)
                const matches = product.c_deliveryDaysText.match(/(\d+)-(\d+)/);
                if (matches && matches.length >= 3) {
                    const minDays = parseInt(matches[1], 10);
                    const maxDays = parseInt(matches[2], 10);

                    console.log(`Extracted min-max days: ${minDays}-${maxDays}`);

                    // Non-standard is anything other than 1-2 hverdage
                    if (minDays !== 1 || maxDays !== 2) {
                        hasNonStandard = true;

                        // Update longest estimate if this is longer
                        const currentEstimate = `${minDays}-${maxDays}`;
                        const [currentMax] = longestEstimate.split('-').map(Number).slice(-1);

                        if (maxDays > currentMax) {
                            longestEstimate = currentEstimate;
                            console.log(`New longest estimate: ${longestEstimate}`);
                        }
                    }
                }
            }

            // Also check c_deliveryDays as backup
            else if (product.c_deliveryDays) {
                console.log(`Product ${product.id} has c_deliveryDays: ${product.c_deliveryDays}`);
                const days = parseInt(product.c_deliveryDays, 10);

                // Non-standard is anything more than 2 days
                if (!isNaN(days) && days > 2) {
                    hasNonStandard = true;

                    // For single numbers, treat as range to max (e.g., 7 becomes 1-7)
                    const currentEstimate = `1-${days}`;
                    const [currentMax] = longestEstimate.split('-').map(Number).slice(-1);

                    if (days > currentMax) {
                        longestEstimate = currentEstimate;
                        console.log(`New longest estimate: ${longestEstimate}`);
                    }
                }
            }
        });

        console.log(
            `Has non-standard delivery items${shipmentId ? ` (shipment: ${shipmentId})` : ''}:`,
            hasNonStandard,
        );
        console.log('Longest delivery estimate:', longestEstimate);

        if (hasNonStandard) {
            setLongestDeliveryEstimate(longestEstimate);
        }

        return hasNonStandard;
    };

    return {
        hasNonStandardDelivery,
        longestDeliveryEstimate,
        isDeliveryDialogOpen,
        setIsDeliveryDialogOpen,
    };
};
